import { OrgChartEmployee } from '../types/orgChart.types';

/**
 * Collects `rootLogin` plus every descendant reachable through `managerLogin` back-pointers
 * in the loaded `employees` map — i.e. the exact subtree the org chart renders under a node.
 *
 * Children are derived the same way `OrgChartTree` derives its visible reports: an employee is
 * a child of `login` when `emp.managerLogin === login`, and manually-added employees are skipped
 * (they render as flat, non-recursive nodes outside the tree). This keeps the collected set in
 * lockstep with what the user actually sees nested beneath the node.
 *
 * The walk is breadth-first and cycle-safe: a login is enqueued only if it isn't already in the
 * result set, so cyclic `managerLogin` data (A→B→A) terminates without duplicates or infinite
 * loops. Only currently-loaded employees are considered — in 'all' mode the whole subtree is
 * loaded upfront and in 'direct' mode the tree is flat, so this covers both live view modes.
 *
 * Pure and React-free.
 */
export function collectSubtreeLogins(rootLogin: string, employees: Map<string, OrgChartEmployee>): Set<string> {
  // Build a managerLogin -> [child logins] adjacency map once, excluding manual adds so the
  // walk matches the tree's visible reports.
  const childrenByManager = new Map<string, string[]>();
  employees.forEach((employee, login) => {
    if (employee.isManuallyAdded || !employee.managerLogin) return;
    const siblings = childrenByManager.get(employee.managerLogin) ?? [];
    siblings.push(login);
    childrenByManager.set(employee.managerLogin, siblings);
  });

  const subtree = new Set<string>([rootLogin]);
  const queue: string[] = [rootLogin];

  while (queue.length > 0) {
    const current = queue.shift() as string;
    const children = childrenByManager.get(current) ?? [];
    for (const child of children) {
      // Enqueue each login at most once; guards against cyclic managerLogin data.
      if (!subtree.has(child)) {
        subtree.add(child);
        queue.push(child);
      }
    }
  }

  return subtree;
}
